import { ResponsiveBar } from "@nivo/bar";
import {
  endOfMonth,
  endOfQuarter,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subDays,
} from "date-fns";
import {
  DollarSign,
  ListFilter,
  Percent,
  ReceiptText,
  Truck,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { type Identifier, useGetIdentity, useGetList } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { LOAD_STATUS_CHOICES } from "../loads/LoadInputs";
import { LEAD_ACTIVE_STATUSES, LEAD_STATUS_CHOICES } from "../leads/leadConsts";
import type { Lead } from "../types";
import type { DashboardLoad } from "./DashboardLoadCard";
import { LoadWorkbench } from "./LoadWorkbench";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const dateKey = (date: Date) => format(date, "yyyy-MM-dd");
const amount = (value?: number | null) => value ?? 0;
const customerTotal = (load: DashboardLoad) =>
  amount(load.customer_total_usd ?? load.customer_invoice_total);
const vendorTotal = (load: DashboardLoad) =>
  amount(load.vendor_total_usd ?? load.vendor_invoice_total);
const statusName = (status: string) =>
  LOAD_STATUS_CHOICES.find((choice) => choice.id === status)?.name ?? status;

const WeightedLoadsCard = ({
  sales,
  purchasing,
  logistics,
}: {
  sales: number;
  purchasing: number;
  logistics: number;
}) => {
  const weightedTotal = sales * 0.4 + purchasing * 0.4 + logistics * 0.2;

  return (
    <Card className="gap-3 py-5">
      <CardHeader className="grid grid-cols-[1fr_auto] px-5">
        <div className="space-y-1">
          <CardDescription>Loads this month</CardDescription>
          <CardTitle className="text-2xl">
            {weightedTotal.toFixed(1)} weighted
          </CardTitle>
        </div>
        <Truck className="size-5 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-2 px-5 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Sales × 40%</span>
          <span className="font-semibold">{sales}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Purchasing × 40%</span>
          <span className="font-semibold">{purchasing}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Logistics × 20%</span>
          <span className="font-semibold">{logistics}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const KpiCard = ({
  title,
  value,
  detail,
  icon: Icon,
}: {
  title: string;
  value: string;
  detail: string;
  icon: typeof Truck;
}) => (
  <Card className="gap-3 py-5">
    <CardHeader className="grid grid-cols-[1fr_auto] px-5">
      <div className="space-y-1">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </div>
      <Icon className="size-5 text-muted-foreground" />
    </CardHeader>
    <CardContent className="px-5 text-xs text-muted-foreground">
      {detail}
    </CardContent>
  </Card>
);

const LoadsTable = ({
  loads,
  accounting = false,
  hideHeader = false,
}: {
  loads: DashboardLoad[];
  accounting?: boolean;
  hideHeader?: boolean;
}) => (
  <Card
    className={`gap-0 overflow-hidden py-0 ${hideHeader ? "rounded-none border-0 shadow-none" : ""}`}
  >
    {!hideHeader && (
      <CardHeader className="border-b py-5">
        <CardTitle>
          {accounting ? "Load financial detail" : "My load detail"}
        </CardTitle>
        <CardDescription>
          Select a load to open its full record.
        </CardDescription>
      </CardHeader>
    )}
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="p-3 text-left font-medium">Load</th>
            <th className="p-3 text-left font-medium">Pickup</th>
            <th className="p-3 text-left font-medium">Customer</th>
            <th className="p-3 text-left font-medium">Status</th>
            {accounting && (
              <th className="p-3 text-right font-medium">Customer total</th>
            )}
            {accounting && (
              <th className="p-3 text-right font-medium">Vendor total</th>
            )}
            <th className="p-3 text-right font-medium">Profit</th>
            <th className="p-3 text-right font-medium">Margin</th>
          </tr>
        </thead>
        <tbody>
          {loads.map((load) => (
            <tr
              key={load.id}
              className="border-b last:border-0 hover:bg-muted/50"
            >
              <td className="p-3">
                <Link
                  className="font-medium text-primary hover:underline"
                  to={`/loads/${load.id}/show`}
                >
                  {load.load_number || load.id}
                </Link>
              </td>
              <td className="p-3 whitespace-nowrap">
                {load.pickup_date
                  ? new Date(
                      `${load.pickup_date}T00:00:00`,
                    ).toLocaleDateString()
                  : "—"}
              </td>
              <td
                className="max-w-52 truncate p-3"
                title={load.customer_company_name}
              >
                {load.customer_company_name || "—"}
              </td>
              <td className="p-3">
                <Badge variant="outline">{statusName(load.status)}</Badge>
              </td>
              {accounting && (
                <td className="p-3 text-right">
                  {currency.format(customerTotal(load))}
                </td>
              )}
              {accounting && (
                <td className="p-3 text-right">
                  {currency.format(vendorTotal(load))}
                </td>
              )}
              <td className="p-3 text-right">
                {currency.format(amount(load.profit_usd))}
              </td>
              <td className="p-3 text-right">
                {load.margin != null ? `${load.margin}%` : "—"}
              </td>
            </tr>
          ))}
          {!loads.length && (
            <tr>
              <td
                className="p-8 text-center text-muted-foreground"
                colSpan={accounting ? 8 : 6}
              >
                No loads in this period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

const SummaryChart = ({
  data,
  keys,
}: {
  data: Record<string, string | number>[];
  keys: string[];
}) => (
  <Card>
    <CardHeader>
      <CardTitle>Charted summary</CardTitle>
      <CardDescription>Financial values shown in USD.</CardDescription>
    </CardHeader>
    <CardContent className="h-80 px-2 sm:px-6">
      <ResponsiveBar
        data={data}
        keys={keys}
        indexBy="label"
        margin={{ top: 10, right: 20, bottom: 45, left: 70 }}
        padding={0.35}
        colors={{ scheme: "set2" }}
        valueFormat={(value) => currency.format(Number(value))}
        axisBottom={{ tickSize: 0, tickPadding: 10 }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          format: (value) => currency.format(Number(value)),
        }}
        enableLabel={false}
        role="application"
        ariaLabel="Dashboard financial summary"
      />
    </CardContent>
  </Card>
);

type LoadOwnerField =
  | "sales_owner_id"
  | "purchasing_owner_id"
  | "logistics_owner_id";

const useQuarterLoads = (
  ownerId?: Identifier,
  ownerField: LoadOwnerField = "sales_owner_id",
) => {
  const today = new Date();
  const quarterStart = startOfQuarter(today);
  const quarterEnd = endOfQuarter(today);
  const query = useGetList<DashboardLoad>("loads_summary", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "pickup_date", order: "DESC" },
    filter: {
      "pickup_date@gte": dateKey(quarterStart),
      ...(ownerId == null ? {} : { [`${ownerField}@eq`]: ownerId }),
    },
  });
  const data = query.data?.filter((load) => {
    if (!load.pickup_date) return false;
    return new Date(`${load.pickup_date}T00:00:00`) <= quarterEnd;
  });
  return { ...query, data };
};

export const AccountManagerDashboard = () => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const { data = [], isPending: salesPending } = useQuarterLoads(
    identity?.id,
    "sales_owner_id",
  );
  const { data: purchasingLoads = [], isPending: purchasingPending } =
    useQuarterLoads(identity?.id, "purchasing_owner_id");
  const { data: logisticsLoads = [], isPending: logisticsPending } =
    useQuarterLoads(identity?.id, "logistics_owner_id");
  const today = new Date();
  const monthStart = startOfMonth(today);
  const monthEnd = endOfMonth(today);
  const isThisMonth = (load: DashboardLoad) => {
    if (!load.pickup_date) return false;
    const pickup = new Date(`${load.pickup_date}T00:00:00`);
    return pickup >= monthStart && pickup <= monthEnd;
  };
  const monthlyLoads = data.filter(isThisMonth);
  const monthlyPurchasingLoads = purchasingLoads.filter(isThisMonth);
  const monthlyLogisticsLoads = logisticsLoads.filter(isThisMonth);
  const quarterProfit = data.reduce(
    (sum, load) => sum + amount(load.profit_usd),
    0,
  );
  const revenue = data.reduce((sum, load) => sum + customerTotal(load), 0);
  const margin = revenue ? (quarterProfit / revenue) * 100 : 0;

  if (identityPending || salesPending || purchasingPending || logisticsPending)
    return null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <WeightedLoadsCard
          sales={monthlyLoads.length}
          purchasing={monthlyPurchasingLoads.length}
          logistics={monthlyLogisticsLoads.length}
        />
        <KpiCard
          title="Loads this quarter"
          value={String(data.length)}
          detail={`${currency.format(revenue)} customer revenue`}
          icon={ReceiptText}
        />
        <KpiCard
          title="Quarter profit"
          value={currency.format(quarterProfit)}
          detail="Across your loads this quarter"
          icon={DollarSign}
        />
        <KpiCard
          title="Quarter margin"
          value={`${margin.toFixed(1)}%`}
          detail="Profit divided by customer revenue"
          icon={Percent}
        />
      </div>
      <LoadWorkbench />
    </div>
  );
};

export const AccountingDashboard = () => {
  const { data = [], isPending } = useQuarterLoads();
  const receivables = data
    .filter((load) => !load.customer_date_paid)
    .reduce((sum, load) => sum + customerTotal(load), 0);
  const payables = data
    .filter((load) => !load.vendor_date_paid)
    .reduce((sum, load) => sum + vendorTotal(load), 0);
  const revenue = data.reduce((sum, load) => sum + customerTotal(load), 0);
  const profit = data.reduce((sum, load) => sum + amount(load.profit_usd), 0);
  const ready = data.filter(
    (load) => load.status === "ready_to_invoice",
  ).length;
  const chartData = [{ label: "This quarter", receivables, payables, profit }];

  if (isPending) return null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Quarter revenue"
          value={currency.format(revenue)}
          detail={`${data.length} loads in the quarter`}
          icon={DollarSign}
        />
        <KpiCard
          title="Open receivables"
          value={currency.format(receivables)}
          detail="Customer totals without a paid date"
          icon={ReceiptText}
        />
        <KpiCard
          title="Open payables"
          value={currency.format(payables)}
          detail="Vendor totals without a paid date"
          icon={ReceiptText}
        />
        <KpiCard
          title="Ready to invoice"
          value={String(ready)}
          detail={`${currency.format(profit)} quarter profit`}
          icon={Truck}
        />
      </div>
      <SummaryChart
        data={chartData}
        keys={["receivables", "payables", "profit"]}
      />
      <LoadsTable loads={data} accounting />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lead generation dashboard
// ---------------------------------------------------------------------------

const ACTIVE_STATUS_IN = `(${LEAD_ACTIVE_STATUSES.map((s) => `"${s}"`).join(",")})`;

const useLeadTotal = (filter: Record<string, unknown>) => {
  const { total, isPending } = useGetList<Lead>("leads", {
    pagination: { page: 1, perPage: 1 },
    filter,
  });
  return { total: total ?? 0, isPending };
};

const leadStatusName = (status: string) =>
  LEAD_STATUS_CHOICES.find((choice) => choice.id === status)?.name ?? status;

const leadName = (lead: Lead) =>
  [lead.first_name, lead.last_name].filter(Boolean).join(" ") ||
  lead.account_name ||
  lead.lead_code ||
  `#${lead.id}`;

const LeadsTable = ({
  leads,
  title,
  description,
}: {
  leads: Lead[];
  title: string;
  description: string;
}) => (
  <Card className="gap-0 overflow-hidden py-0">
    <CardHeader className="border-b py-5">
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="p-3 text-left font-medium">Lead #</th>
            <th className="p-3 text-left font-medium">Name</th>
            <th className="p-3 text-left font-medium">Account</th>
            <th className="p-3 text-left font-medium">Status</th>
            <th className="p-3 text-left font-medium">Source</th>
            <th className="p-3 text-left font-medium">Created</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr
              key={lead.id}
              className="border-b last:border-0 hover:bg-muted/50"
            >
              <td className="p-3 whitespace-nowrap">
                <Link
                  className="font-medium text-primary hover:underline"
                  to={`/leads/${lead.id}/show`}
                >
                  {lead.lead_code || lead.id}
                </Link>
              </td>
              <td className="max-w-40 truncate p-3">{leadName(lead)}</td>
              <td className="max-w-40 truncate p-3">
                {lead.account_name || "—"}
              </td>
              <td className="p-3 whitespace-nowrap">
                <Badge variant="outline">{leadStatusName(lead.status)}</Badge>
              </td>
              <td className="max-w-40 truncate p-3">
                {lead.lead_source || "—"}
              </td>
              <td className="p-3 whitespace-nowrap">
                {lead.created_at
                  ? new Date(lead.created_at).toLocaleDateString()
                  : "—"}
              </td>
            </tr>
          ))}
          {!leads.length && (
            <tr>
              <td className="p-8 text-center text-muted-foreground" colSpan={6}>
                No leads found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

export const LeadGenDashboard = () => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const weekStart = format(startOfWeek(new Date()), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
  const staleBefore = format(subDays(new Date(), 7), "yyyy-MM-dd");

  const newThisWeek = useLeadTotal({
    status: "New",
    "created_at@gte": weekStart,
  });
  const openPipeline = useLeadTotal({ "status@in": ACTIVE_STATUS_IN });
  const myOpen = useLeadTotal({
    "status@in": ACTIVE_STATUS_IN,
    ...(identity?.id == null ? {} : { sales_id: identity.id }),
  });
  const unassigned = useLeadTotal({
    status: "New",
    "sales_id@is": "null",
  });
  const convertedThisMonth = useLeadTotal({
    status: "Converted",
    "converted_at@gte": monthStart,
  });
  const createdThisMonth = useLeadTotal({
    "created_at@gte": monthStart,
  });

  const { data: newLeads = [], isPending: newPending } = useGetList<Lead>(
    "leads",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "created_at", order: "DESC" },
      filter: { status: "New" },
    },
  );

  const { data: staleLeads = [], isPending: stalePending } = useGetList<Lead>(
    "leads",
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "updated_at", order: "ASC" },
      filter: {
        "status@in": `("Follow-up","Contact Made","No Answer or Busy")`,
        "updated_at@lt": staleBefore,
      },
    },
  );

  const { data: converted = [], isPending: convertedPending } =
    useGetList<Lead>("leads", {
      pagination: { page: 1, perPage: 10 },
      sort: { field: "converted_at", order: "DESC" },
      filter: { "converted_company_id@not.is": "null" },
    });

  const isPending =
    identityPending ||
    newThisWeek.isPending ||
    openPipeline.isPending ||
    myOpen.isPending ||
    unassigned.isPending ||
    convertedThisMonth.isPending ||
    createdThisMonth.isPending ||
    newPending ||
    stalePending ||
    convertedPending;

  if (isPending) return null;

  const conversionRate = createdThisMonth.total
    ? ((convertedThisMonth.total / createdThisMonth.total) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="New this week"
          value={String(newThisWeek.total)}
          detail={`${unassigned.total} unassigned new leads`}
          icon={UserPlus}
        />
        <KpiCard
          title="Open pipeline"
          value={String(openPipeline.total)}
          detail="Leads in active statuses"
          icon={ListFilter}
        />
        <KpiCard
          title="My open leads"
          value={String(myOpen.total)}
          detail="Active statuses assigned to you"
          icon={Users}
        />
        <KpiCard
          title="Converted this month"
          value={String(convertedThisMonth.total)}
          detail={`${conversionRate}% of ${createdThisMonth.total} leads created this month`}
          icon={UserCheck}
        />
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <LeadsTable
          leads={newLeads}
          title="Newest leads"
          description="Latest New leads — claim and work them."
        />
        <LeadsTable
          leads={staleLeads}
          title="Stale follow-ups"
          description="Contact Made / Follow-up leads untouched for 7+ days."
        />
      </div>
      <LeadsTable
        leads={converted}
        title="Recently converted"
        description="Leads converted into accounts and contacts."
      />
    </div>
  );
};
